/** Theme palettes — mirrors Android Palette.kt exactly. */

export interface Palette {
  name: string;
  label: string;
  bg: string;
  obsidian: string;
  obsidian2: string;
  panel: string;
  panel2: string;
  edge: string;
  cream: string;
  creamDim: string;
  muted: string;
  mutedLight: string;
  amber: string;
  amberHot: string;
  amberDim: string;
  cyan: string;
  lime: string;
  redline: string;
  glow: string;
  icon: string;
}

export const DARK: Palette = {
  name: "dark", label: "Dark",
  bg: "#0a0b0d", obsidian: "#0a0b0d", obsidian2: "#101216", panel: "#17181b", panel2: "#1e2024",
  edge: "#2a2d33", cream: "#f1ebe2", creamDim: "#c7c1b7", muted: "#8a8680", mutedLight: "#c7c1b7",
  amber: "#ffb200", amberHot: "#ff8a00", amberDim: "rgba(255,178,0,0.4)", cyan: "#4fc3f7", lime: "#a6e22e", redline: "#ff5a52",
  // Header logo stays amber on the default Dark theme.
  glow: "rgba(255,178,0,0.16)", icon: "#ffb200",
};

export const LIGHT: Palette = {
  name: "light", label: "Light",
  bg: "#f8f8f6", obsidian: "#f8f8f6", obsidian2: "#f0f0ee", panel: "#e8e8e5", panel2: "#e0e0dd",
  edge: "#d0d0ce", cream: "#1c1c1a", creamDim: "#4a4a48", muted: "#8a8a86", mutedLight: "#b0b0ac",
  amber: "#d49000", amberHot: "#d97a00", amberDim: "rgba(212,144,0,0.3)", cyan: "#0097a7", lime: "#558b2f", redline: "#c62828",
  glow: "rgba(212,144,0,0.12)", icon: "#1c1c1a",
};

export const CHROME: Palette = {
  name: "chrome", label: "Chrome",
  bg: "#f5f5f3", obsidian: "#f5f5f3", obsidian2: "#ededeb", panel: "#e5e5e2", panel2: "#ddddda",
  edge: "#cfcfcc", cream: "#1c1c1a", creamDim: "#4a4a48", muted: "#8a8a86", mutedLight: "#b0b0ac",
  amber: "#5a5a56", amberHot: "#3a3a38", amberDim: "rgba(90,90,86,0.3)", cyan: "#6a6a68", lime: "#4a4a48", redline: "#8a8a88",
  glow: "rgba(90,90,86,0.12)", icon: "#1c1c1a",
};

export const GREEN: Palette = {
  name: "green", label: "Green",
  bg: "#0a120a", obsidian: "#0a120a", obsidian2: "#0e180e", panel: "#122012", panel2: "#162616",
  edge: "#1e301e", cream: "#e8f0e8", creamDim: "#a0b8a0", muted: "#6a806a", mutedLight: "#8aa08a",
  amber: "#4caf50", amberHot: "#66bb6a", amberDim: "rgba(76,175,80,0.3)", cyan: "#81c784", lime: "#a5d6a7", redline: "#69f0ae",
  glow: "rgba(76,175,80,0.16)", icon: "#4caf50",
};

export const RED: Palette = {
  name: "red", label: "Red",
  bg: "#120a0a", obsidian: "#120a0a", obsidian2: "#1a0e0e", panel: "#221212", panel2: "#2a1616",
  edge: "#3a1e1e", cream: "#f0e8e8", creamDim: "#b8a0a0", muted: "#806a6a", mutedLight: "#a08a8a",
  amber: "#e53935", amberHot: "#ff5252", amberDim: "rgba(229,57,53,0.3)", cyan: "#ef5350", lime: "#ff8a80", redline: "#ff1744",
  glow: "rgba(229,57,53,0.16)", icon: "#e53935",
};

export const CUSTOM: Palette = {
  name: "custom", label: "Custom",
  bg: "#0a0b0d", obsidian: "#0a0b0d", obsidian2: "#101216", panel: "#17181b", panel2: "#1e2024",
  edge: "#2a2d33", cream: "#f1ebe2", creamDim: "#c7c1b7", muted: "#8a8680", mutedLight: "#c7c1b7",
  amber: "#ffb200", amberHot: "#ff8a00", amberDim: "rgba(255,178,0,0.4)", cyan: "#4fc3f7", lime: "#a6e22e", redline: "#ff5a52",
  glow: "rgba(255,178,0,0.16)", icon: "#f1ebe2",
};

export const THEMES: Palette[] = [DARK, LIGHT, CHROME, GREEN, RED];

const STORAGE_KEY = "carspend-theme";
const CUSTOM_STORAGE_KEY = "carspend-custom-theme";

const COLOUR_KEYS = ["obsidian", "obsidian2", "panel", "panel2", "edge", "cream", "creamDim", "muted", "mutedLight", "amber", "amberHot", "cyan", "redline", "lime"] as const;

const DESCRIPTIONS: Record<string, string> = {
  obsidian: "Deepest background (main screen background)",
  obsidian2: "Slightly lighter background (cards, panels)",
  panel: "Card/section background",
  panel2: "Elevated card background",
  edge: "Borders and dividers",
  cream: "Primary text colour",
  creamDim: "Secondary/subtitle text colour",
  muted: "Muted/tertiary text colour",
  mutedLight: "Disabled/low-contrast text colour",
  amber: "Primary accent colour (buttons, highlights, active states)",
  amberHot: "Hot accent (hovered/pressed states, sparklines)",
  cyan: "Data accent (secondary charts, info elements)",
  redline: "Danger/error colour (delete actions, warnings)",
  lime: "Positive/confirmation accent (savings, success states)",
};

export function loadTheme(): Palette {
  try {
    const name = localStorage.getItem(STORAGE_KEY);
    if (name === "custom") {
      const json = localStorage.getItem(CUSTOM_STORAGE_KEY);
      if (json) {
        const parsed = parseCustomPalette(json);
        if (parsed) return { ...parsed, name: "custom", label: "Custom" };
      }
      return CUSTOM;
    }
    if (name) return THEMES.find((t) => t.name === name) ?? DARK;
  } catch {}
  return DARK;
}

export function saveTheme(palette: Palette) {
  try {
    localStorage.setItem(STORAGE_KEY, palette.name);
    if (palette.name === "custom") {
      localStorage.setItem(CUSTOM_STORAGE_KEY, paletteToJson(palette));
    }
  } catch {}
}

export function applyTheme(palette: Palette) {
  const r = document.documentElement;
  r.style.setProperty("--obsidian", palette.obsidian);
  r.style.setProperty("--obsidian2", palette.obsidian2);
  r.style.setProperty("--panel", palette.panel);
  r.style.setProperty("--panel2", palette.panel2);
  r.style.setProperty("--edge", palette.edge);
  r.style.setProperty("--cream", palette.cream);
  r.style.setProperty("--creamDim", palette.creamDim);
  r.style.setProperty("--muted", palette.muted);
  r.style.setProperty("--mutedLight", palette.mutedLight);
  r.style.setProperty("--amber", palette.amber);
  r.style.setProperty("--amberHot", palette.amberHot);
  r.style.setProperty("--amberDim", palette.amberDim);
  r.style.setProperty("--cyan", palette.cyan);
  r.style.setProperty("--lime", palette.lime);
  r.style.setProperty("--redline", palette.redline);
  r.style.setProperty("--glow", palette.glow);
  r.style.setProperty("--icon", palette.icon);
  document.body.style.background = palette.bg;
}

/** Parse a custom palette JSON — accepts flat map or nested template format. */
export function parseCustomPalette(json: string): Palette | null {
  try {
    const root = JSON.parse(json) as Record<string, unknown>;
    const source: Record<string, unknown> = (root.palette && typeof root.palette === "object" ? root.palette : root) as Record<string, unknown>;
    const map: Record<string, string> = {};
    for (const key of Object.keys(source)) {
      if (!(COLOUR_KEYS as readonly string[]).includes(key)) continue;
      const val = source[key];
      let hex: string | null = null;
      if (typeof val === "string") hex = val;
      else if (val && typeof val === "object" && typeof (val as Record<string, unknown>).value === "string") hex = (val as Record<string, unknown>).value as string;
      if (hex) map[key] = hex;
    }
    if (Object.keys(map).length === 0) return null;
    const d = DARK;
    const color = (k: string, fb: string) => {
      const hex = map[k];
      if (!hex) return fb;
      const cleaned = hex.replace(/^#/, "").replace(/^FF/, "");
      if (/^[0-9a-fA-F]{8}$/.test(cleaned)) {
        const r = parseInt(cleaned.slice(0, 2), 16);
        const g = parseInt(cleaned.slice(2, 4), 16);
        const b = parseInt(cleaned.slice(4, 6), 16);
        const a = parseInt(cleaned.slice(6, 8), 16) / 255;
        return `rgba(${r},${g},${b},${a.toFixed(2)})`;
      }
      if (/^[0-9a-fA-F]{6}$/.test(cleaned)) return `#${cleaned}`;
      return fb;
    };
    return {
      name: "custom", label: "Custom",
      bg: color("obsidian", d.bg),
      obsidian: color("obsidian", d.obsidian),
      obsidian2: color("obsidian2", d.obsidian2),
      panel: color("panel", d.panel),
      panel2: color("panel2", d.panel2),
      edge: color("edge", d.edge),
      cream: color("cream", d.cream),
      creamDim: color("creamDim", d.creamDim),
      muted: color("muted", d.muted),
      mutedLight: color("mutedLight", d.mutedLight),
      amber: color("amber", d.amber),
      amberHot: color("amberHot", d.amberHot),
      amberDim: color("amber", d.amberDim),
      cyan: color("cyan", d.cyan),
      lime: color("lime", d.lime),
      redline: color("redline", d.redline),
      glow: color("amber", d.glow),
      icon: color("cream", d.icon),
    };
  } catch {
    return null;
  }
}

/** Serialize palette to flat JSON. */
export function paletteToJson(p: Palette): string {
  const map: Record<string, string> = {};
  for (const k of COLOUR_KEYS) map[k] = (p as unknown as Record<string, string>)[k] ?? "";
  return JSON.stringify(map, null, 2);
}

/** Export a template JSON with descriptions (for user editing). */
export function paletteToTemplateJson(p: Palette): string {
  const palette: Record<string, { value: string; description: string }> = {};
  for (const k of COLOUR_KEYS) {
    palette[k] = { value: (p as unknown as Record<string, string>)[k] ?? "", description: DESCRIPTIONS[k] ?? "" };
  }
  return JSON.stringify({
    palette,
    _instructions: "Edit the hex colour values (format: #RRGGBB or #AARRGGBB) above, save as .json, and import in the app.",
  }, null, 2);
}
